import { Route, Routes } from '@angular/router';

/**
 * The Dashboard's feature index, DERIVED FROM THE ROUTE TABLE.
 *
 * A page declares itself once, on its route:
 *
 *   { path: 'customers', component: CustomersComponent,
 *     canActivate: [AuthGuard, PermissionGuard],
 *     data: { title: 'Customers', icon: 'people', group: 'CRM',
 *             description: 'Accounts, contacts, and service addresses',
 *             permission: 'Customer.View' } }
 *
 * — and that one declaration drives the guard, the Dashboard card, and the
 * breadcrumb. Card visibility used to be a parallel
 * hand-written list mirroring the guards, and the two had drifted: eighteen
 * routes were hidden on the Dashboard while their URLs stayed open to anyone
 * signed in. Nothing here decides permissions — it only reads what the route
 * declared.
 *
 * Only a group's own presentation (icon, colour, blurb, order) lives here: it
 * belongs to the group, not to any single route.
 */

/** Route `data` contract for a page that appears on the Dashboard. */
export interface FeatureRouteData {
  title?: string;
  icon?: string;
  group?: string;
  description?: string;
  /** Set false for pages reached from a section-local menu rather than a
   *  standalone Dashboard card. The group still drives breadcrumbs. */
  showInDashboard?: boolean;
  /** Sort rank within the group's Dashboard panel (lower = earlier). Unset = 100,
   *  so unranked items keep their route-table order after any ranked ones. */
  order?: number;
  /** Bucket within the group (see GROUP_STAGES). Groups whose items are
   *  bucketed render as stage columns instead of a flat card grid — a
   *  left-to-right pipeline when the group is a workflow, plain sections when
   *  it is a taxonomy. */
  stage?: string;
  permission?: string;
  permissionAny?: string[];
}

export interface FeatureItem {
  label: string;
  description: string;
  icon: string;
  route: string;
}

export interface FeatureStage {
  title: string;
  items: FeatureItem[];
}

export interface FeatureGroup {
  title: string;
  icon: string;
  description: string;
  color: string;
  items: FeatureItem[];
  /** Present when the group's items are bucketed: ordered stages, each with
   *  its cards. Presentation surfaces can use these buckets to group a menu or
   *  panel without maintaining a second feature list. Undefined = flat. */
  stages?: FeatureStage[];
}

/**
 * The groups whose items are bucketed instead of flat, in display order.
 *
 * Items name their bucket through route data `stage`. Anything with no or
 * unknown stage falls into a trailing "More" bucket, so a page can never
 * disappear from the Dashboard by being mis-stamped or unstamped.
 */
const GROUP_STAGES: Record<string, string[]> = {
  'Configuration': [
    'Customers & Billing',
    'Leads & Routing',
    'Shared Catalogs',
    'System',
  ],
};

/**
 * Group presentation and display order. A route naming a group that is absent
 * here still shows up — under its own name, at the end — so adding a page can
 * never make it invisible.
 */
const GROUP_META: { title: string; icon: string; description: string; color: string }[] = [
  { title: 'CRM',                  icon: 'people',         description: 'Customers, contacts, cases & contracts',                         color: '#1565c0' },
  // Must match the `group` a route declares, exactly. A group with no entry
  // here still renders - under its own name, at the end, with no icon or
  // colour - so a mismatch degrades quietly instead of failing.
  { title: 'Sales',                icon: 'trending_up',    description: 'Lead discovery, lead management, roll-off deals', color: '#6a1b9a' },
  { title: 'Smart Workflow',       icon: 'account_tree',   description: 'Published workflows across your business',                        color: 'var(--br-color-muted)' },
  { title: 'Configuration',        icon: 'settings',       description: 'Lookups & catalogs',                                             color: '#616161' },
];

function dataOf(route: Route): FeatureRouteData {
  return (route.data ?? {}) as FeatureRouteData;
}

/** True when the caller holds what the route requires (nothing = open to all). */
function permitted(data: FeatureRouteData, has: (perm: string) => boolean): boolean {
  if (data.permission) return has(data.permission);
  if (data.permissionAny) return data.permissionAny.some(has);
  return true;
}

function itemOf(route: Route): FeatureItem {
  const d = dataOf(route);
  return {
    label: d.title ?? '',
    description: d.description ?? '',
    icon: d.icon ?? 'chevron_right',
    route: '/' + (route.path ?? ''),
  };
}

/** Every route that asked to appear on the Dashboard. */
function indexedRoutes(routes: Routes): Route[] {
  return routes.filter(r => {
    const data = dataOf(r);
    return !!data.title && !!data.group && data.showInDashboard !== false;
  });
}

/** Groups with their items permission-filtered; empty groups removed. */
export function visibleGroups(routes: Routes, has: (perm: string) => boolean): FeatureGroup[] {
  const allowed = indexedRoutes(routes).filter(r => permitted(dataOf(r), has));
  const order = GROUP_META.map(g => g.title);
  const rank = (name: string) => {
    const i = order.indexOf(name);
    return i < 0 ? order.length : i;
  };

  const names = [...new Set(allowed.map(r => dataOf(r).group!))].sort((a, b) => rank(a) - rank(b));

  return names.map(name => {
    const meta = GROUP_META.find(g => g.title === name);
    const groupRoutes = allowed
      .filter(r => dataOf(r).group === name)
      .sort((a, b) => (dataOf(a).order ?? 100) - (dataOf(b).order ?? 100));

    // Bucketed groups: file items into the stages the group declared.
    let stages: FeatureStage[] | undefined;
    const stageOrder = GROUP_STAGES[name];
    if (stageOrder) {
      stages = stageOrder.map(s => ({
        title: s,
        items: groupRoutes.filter(r => dataOf(r).stage === s).map(itemOf),
      }));
      const orphans = groupRoutes.filter(r => !stageOrder.includes(dataOf(r).stage ?? ''));
      if (orphans.length) stages.push({ title: 'More', items: orphans.map(itemOf) });
      stages = stages.filter(s => s.items.length);
    }

    return {
      title: name,
      icon: meta?.icon ?? 'folder',
      description: meta?.description ?? '',
      color: meta?.color ?? '#607d8b',
      items: groupRoutes.map(itemOf),
      stages,
    };
  });
}
